/**
 * My Health Record Emergency Access declaration.
 *
 * Emergency access overrides the access controls an individual has set on their
 * own record. Software that offers it must put the declaration below in front
 * of the clinician and take a positive confirmation before invoking it — the
 * clinician is making a legal declaration, not dismissing a warning.
 *
 * Background: OAIC, "My Health Record emergency access function"
 * https://www.oaic.gov.au/privacy/my-health-record/my-health-record-emergency-access-function
 *
 * WORDING IS VERBATIM as communicated for primary care and is deliberately not
 * paraphrased or modernised. It says "eHealth record" because that was the
 * system's name when the requirement was issued; the record is now called My
 * Health Record. Changing a declaration a clinician is legally attesting to is
 * a compliance decision, not a copy edit — if it should be restated in current
 * terminology, that call belongs with whoever owns the obligation, and this is
 * the single place to change it.
 */

export const EMERGENCY_ACCESS_DECLARATION = [
  "By selecting Emergency Access, you are declaring that access to this eHealth record is necessary to lessen or prevent a serious threat to an individual's life, health or safety or to public health or public safety and your patient's consent cannot be obtained.",
  'This will override any access controls set by the individual and will permit access to all active documents for 5 days.',
  "Your Emergency Access will be recorded on the eHealth Record's audit log and the individual may be notified."
];

export const EMERGENCY_ACCESS_QUESTION = 'Do you wish to proceed?';

export default function EmergencyAccessDialog({ open, onConfirm, onCancel, busy }) {
  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="My Health Record Emergency Access declaration"
      onClick={e => { if (e.target === e.currentTarget && !busy) onCancel(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 3000,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px'
      }}>
      <div style={{
        maxWidth: '560px', width: '100%', maxHeight: '86vh', overflowY: 'auto',
        background: '#15151c', border: '1px solid rgba(239,68,68,0.5)',
        borderRadius: '12px', padding: '22px 24px', textAlign: 'left',
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '14px' }}>
          <span style={{
            background: '#ef4444', color: '#fff', fontSize: '10px', fontWeight: 800,
            letterSpacing: '0.6px', padding: '3px 8px', borderRadius: '4px'
          }}>
            EMERGENCY ACCESS
          </span>
          <h2 style={{ fontSize: '14px', color: '#eee', margin: 0, fontWeight: 700 }}>
            My Health Record declaration
          </h2>
        </div>

        {EMERGENCY_ACCESS_DECLARATION.map((para, i) => (
          <p key={i} style={{
            fontSize: '13px', lineHeight: 1.6, color: '#d6d6de',
            margin: '0 0 12px'
          }}>
            {para}
          </p>
        ))}

        <p style={{ fontSize: '13px', lineHeight: 1.6, color: '#eee', fontWeight: 700, margin: '0 0 18px' }}>
          {EMERGENCY_ACCESS_QUESTION}
        </p>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button
            onClick={onCancel}
            disabled={busy}
            style={{
              padding: '9px 18px', borderRadius: '7px', minHeight: '40px',
              background: 'transparent', border: '1px solid rgba(255,255,255,0.2)',
              color: '#bbb', fontSize: '13px', fontWeight: 600,
              cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1
            }}>
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            style={{
              padding: '9px 18px', borderRadius: '7px', minHeight: '40px',
              background: '#ef4444', border: 'none', color: '#fff',
              fontSize: '13px', fontWeight: 800,
              cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1
            }}>
            {busy ? 'Recording…' : 'Yes, proceed'}
          </button>
        </div>
      </div>
    </div>
  );
}
